import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtDate, fmtKg, batimentColor } from "@/lib/helpers";
import { getQueue, onQueueChange, syncQueue, removeFromQueue } from "@/lib/offlineQueue";
import { Button } from "@/components/ui/button";
import { PlusCircle, RefreshCw, Wifi, WifiOff, CheckCircle2, Clock, Tag, Trash2 } from "lucide-react";
import { toast } from "sonner";

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function ProductionJour() {
  const navigate = useNavigate();
  const [palettes, setPalettes] = useState(null);
  const [queue, setQueue] = useState(getQueue());
  const [online, setOnline] = useState(navigator.onLine);
  const [syncing, setSyncing] = useState(false);

  const load = () => api.get("/palettes", { params: { cree_le: todayISO() } }).then((r) => setPalettes(r.data)).catch(() => setPalettes((p) => p || []));

  useEffect(() => {
    load();
    const off = onQueueChange(() => { setQueue(getQueue()); load(); });
    const on = () => setOnline(true);
    const offl = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", offl);
    const t = setInterval(load, 20000);
    return () => { off(); window.removeEventListener("online", on); window.removeEventListener("offline", offl); clearInterval(t); };
  }, []);

  const sync = async () => {
    setSyncing(true);
    const r = await syncQueue();
    setSyncing(false);
    if (r.done) toast.success(`${r.done} palette(s) synchronisée(s)`);
    else if (r.failed) toast.error("Synchronisation impossible pour le moment");
    else toast("Rien à synchroniser");
  };

  if (!palettes) return <div className="text-slate-400">Chargement…</div>;

  const nbP = palettes.filter((p) => p.type === "P").length;
  const nbD = palettes.length - nbP;
  const net = palettes.reduce((s, p) => s + p.poids_net, 0);

  return (
    <div className="space-y-5" data-testid="production-jour">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading font-black text-3xl uppercase tracking-tight text-slate-900">Production du jour</h1>
          <p className="text-slate-500 font-medium text-sm">{fmtDate(todayISO())} — palettes enregistrées aujourd'hui</p>
        </div>
        <div className="flex items-center gap-3">
          <span data-testid="network-status" className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide ${online ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
            {online ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />} {online ? "Connecté" : "Hors-ligne"}
          </span>
          <Button data-testid="production-new-palette-btn" onClick={() => navigate("/nouvelle-palette")} className="h-12 bg-emerald-600 hover:bg-emerald-700 font-bold">
            <PlusCircle className="w-5 h-5 mr-1" /> Nouvelle palette
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Stat testId="prod-count-total" label="Palettes" value={palettes.length} sub={`${net.toFixed(1)} kg net`} />
        <Stat testId="prod-count-conforme" label="Conformes" value={nbP} tone="emerald" />
        <Stat testId="prod-count-declassee" label="Déclassées" value={nbD} tone="rose" />
      </div>

      {queue.length > 0 && (
        <div data-testid="offline-queue" className="rounded-2xl border-2 border-amber-400 bg-amber-50 p-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 font-heading font-black uppercase tracking-wide text-amber-900">
              <Clock className="w-5 h-5" /> {queue.length} palette{queue.length > 1 ? "s" : ""} en attente de synchronisation
            </div>
            <Button data-testid="offline-sync-btn" onClick={sync} disabled={syncing || !online} variant="outline" className="h-11 font-bold border-amber-400 bg-white">
              <RefreshCw className={`w-5 h-5 mr-1 ${syncing ? "animate-spin" : ""}`} /> Synchroniser maintenant
            </Button>
          </div>
          <div className="space-y-2">
            {queue.map((it) => (
              <div key={it.local_id} data-testid={`offline-item-${it.local_id}`} className="rounded-xl bg-white border border-amber-200 p-3 flex flex-wrap items-center gap-3 text-sm">
                <span className={`px-2 py-0.5 rounded font-bold text-xs uppercase ${it.payload.type === "P" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>
                  {it.payload.type === "P" ? "Conforme" : "Déclassée"}
                </span>
                <span className={`px-2 py-0.5 rounded font-bold text-xs ${batimentColor(it.payload.batiment)}`}>{it.payload.batiment}</span>
                <span className="text-slate-600">{fmtDate(it.payload.date_ponte)}</span>
                {it.payload.motif && <span className="text-rose-700 text-xs font-bold">{it.payload.motif}</span>}
                <span className="font-mono font-bold">{(it.payload.poids_brut - it.payload.tare).toFixed(1)} kg net</span>
                <span className="text-xs text-slate-400 font-mono ml-auto">{it.created_at.slice(11, 16)}</span>
                {it.error && (
                  <span className="w-full text-xs text-rose-700 font-bold flex items-center justify-between gap-2">
                    Refusée : {String(it.error)}
                    <button onClick={() => removeFromQueue(it.local_id)} className="inline-flex items-center gap-1 underline"><Trash2 className="w-3.5 h-3.5" /> Retirer</button>
                  </span>
                )}
              </div>
            ))}
          </div>
          <p className="text-xs text-amber-800 font-medium">Le numéro de palette et l'étiquette seront disponibles après synchronisation (automatique dès le retour du réseau).</p>
        </div>
      )}

      <div className="rounded-2xl bg-white border-2 border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b-2 border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500 font-bold">
                <th className="px-4 py-3">Heure</th>
                <th className="px-4 py-3">N° Palette</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Bât.</th>
                <th className="px-4 py-3 text-right">Net</th>
                <th className="px-4 py-3">Par</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {palettes.map((p) => (
                <tr key={p.id} data-testid={`prod-row-${p.code_qr}`} className={`border-b border-slate-100 ${p.type === "DECLASSEE" ? "bg-rose-50/50" : ""}`}>
                  <td className="px-4 py-3 font-mono text-slate-500">{new Date(p.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</td>
                  <td className="px-4 py-3 font-mono font-bold text-slate-900">{p.code_qr}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded font-bold text-xs uppercase ${p.type === "P" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>
                      {p.type === "P" ? "Conforme" : "Déclassée"}
                    </span>
                  </td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded font-bold text-xs ${batimentColor(p.batiment)}`}>{p.batiment}</span></td>
                  <td className="px-4 py-3 text-right font-mono font-bold">{fmtKg(p.poids_net)}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{p.created_by}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 text-emerald-700 font-bold text-xs uppercase"><CheckCircle2 className="w-4 h-4" /> Enregistrée</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button data-testid={`prod-label-${p.code_qr}`} variant="outline" size="sm" onClick={() => navigate(`/palette/${p.id}/etiquette`)} className="font-bold">
                      <Tag className="w-4 h-4 mr-1" /> Étiquette
                    </Button>
                  </td>
                </tr>
              ))}
              {palettes.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400 font-medium">Aucune palette enregistrée aujourd'hui.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, tone, testId }) {
  const color = { emerald: "text-emerald-700", rose: "text-rose-700" }[tone] || "text-slate-900";
  return (
    <div data-testid={testId} className="rounded-2xl bg-white border-2 border-slate-200 p-4">
      <div className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-400 font-bold">{label}</div>
      <div className={`font-mono font-black text-3xl sm:text-4xl ${color}`}>{value}</div>
      {sub && <div className="text-xs text-slate-400 font-semibold font-mono">{sub}</div>}
    </div>
  );
}
